import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Chrome } from "lucide-react";

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const candidateCtx = Boolean(localStorage.getItem("its_tenant_slug"));

  const goByRole = (u) => navigate(
    u.must_change_password ? "/cambia-password"
      : u.role === "super_admin" ? "/superadmin" : u.role === "admin" ? "/admin" : "/dashboard",
    { replace: true });

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(loginForm.email, loginForm.password);
      goByRole(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await register(regForm.email, regForm.password, regForm.name);
      toast.success("Registrazione completata");
      goByRole(u);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const doForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="text-center mb-8 fade-up">
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Area riservata</h1>
          <p className="mt-2 text-sm text-slate-600">
            {candidateCtx ? "Accedi o registrati per presentare la tua candidatura ITS" : "Accedi con le credenziali fornite"}
          </p>
        </div>
        <Card className="institutional-card fade-up">
          <CardContent className="p-6 sm:p-8">
            {forgotMode ? (
              <div data-testid="auth-forgot-form">
                <h2 className="font-display font-semibold text-lg text-slate-900 mb-4">Password dimenticata</h2>
                {forgotSent ? (
                  <p data-testid="auth-forgot-success" className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
                    Se l'indirizzo è registrato, riceverai a breve un'email con il link per reimpostare la password
                    (valido 1 ora, utilizzabile una sola volta).
                  </p>
                ) : (
                  <form onSubmit={doForgot} className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Inserisci l'email del tuo account: ti invieremo un link per scegliere una nuova password.
                    </p>
                    <div>
                      <Label htmlFor="forgot-email">Email</Label>
                      <Input data-testid="auth-forgot-email" id="forgot-email" type="email" required value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)} />
                    </div>
                    {error && <p data-testid="auth-forgot-error" className="text-sm text-red-600">{error}</p>}
                    <Button data-testid="auth-forgot-submit" type="submit" disabled={busy} className="w-full bg-[#003366] hover:bg-[#002244]">
                      {busy ? "Invio in corso..." : "Invia email di reset"}
                    </Button>
                  </form>
                )}
                <button type="button" data-testid="auth-forgot-back"
                  className="mt-4 text-xs text-slate-500 hover:text-slate-800 w-full text-center"
                  onClick={() => { setForgotMode(false); setForgotSent(false); setError(""); }}>
                  Torna al login
                </button>
              </div>
            ) : (
              <>
                <Tabs defaultValue="login">
                  <TabsList className={`grid ${candidateCtx ? "grid-cols-2" : "grid-cols-1"} w-full`}>
                    <TabsTrigger data-testid="auth-tab-login" value="login">Accedi</TabsTrigger>
                    {candidateCtx && <TabsTrigger data-testid="auth-tab-register" value="register">Registrati</TabsTrigger>}
                  </TabsList>
                  <TabsContent value="login">
                    <form onSubmit={doLogin} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="login-email">Email</Label>
                        <Input data-testid="auth-login-email" id="login-email" type="email" required value={loginForm.email}
                          onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                      </div>
                      <div>
                        <Label htmlFor="login-password">Password</Label>
                        <Input data-testid="auth-login-password" id="login-password" type="password" required value={loginForm.password}
                          onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                      </div>
                      <div className="text-right">
                        <button type="button" data-testid="auth-forgot-link" className="text-xs text-[#003366] hover:underline"
                          onClick={() => { setForgotMode(true); setError(""); }}>
                          Password dimenticata?
                        </button>
                      </div>
                      {error && <p data-testid="auth-error-message" className="text-sm text-red-600">{error}</p>}
                      <Button data-testid="auth-submit-button" type="submit" disabled={busy} className="w-full bg-[#003366] hover:bg-[#002244]">
                        {busy ? "Accesso..." : "Accedi"}
                      </Button>
                    </form>
                  </TabsContent>
                  {candidateCtx && (
                    <TabsContent value="register">
                      <form onSubmit={doRegister} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="reg-name">Nome e Cognome</Label>
                          <Input data-testid="auth-register-name" id="reg-name" required value={regForm.name}
                            onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
                        </div>
                        <div>
                          <Label htmlFor="reg-email">Email</Label>
                          <Input data-testid="auth-register-email" id="reg-email" type="email" required value={regForm.email}
                            onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                        </div>
                        <div>
                          <Label htmlFor="reg-password">Password (min. 8 caratteri)</Label>
                          <Input data-testid="auth-register-password" id="reg-password" type="password" required minLength={8} value={regForm.password}
                            onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                        </div>
                        {error && <p data-testid="auth-register-error" className="text-sm text-red-600">{error}</p>}
                        <Button data-testid="auth-register-submit" type="submit" disabled={busy} className="w-full bg-[#003366] hover:bg-[#002244]">
                          {busy ? "Registrazione..." : "Crea account"}
                        </Button>
                      </form>
                    </TabsContent>
                  )}
                </Tabs>
                {candidateCtx && (
                  <>
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                      <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">oppure</span></div>
                    </div>
                    <Button data-testid="auth-google-button" variant="outline" className="w-full" onClick={loginWithGoogle}>
                      <Chrome className="h-4 w-4 mr-2" /> Continua con Google
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
